import { array, bool, func, object, string } from 'prop-types';
import Placeholder from 'ps-components/Placeholder/Placeholder';
import cn from 'classnames';
import { Button, ButtonGroup, Input, Select } from '../../ps-aspect-design';
import React, { useEffect, useMemo, useState } from 'react';
import InPlaceIndicator from '../LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import AutoCompleteWrapper from './AutoCompleteWrapper';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';
import './FieldFilter.scss';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { numberOptions, textOptions } from './FieldFilterService';

FieldFilters.propTypes = {
  loading: bool,
  loaded: bool,
  elements: object,
  currentFilters: array,
  noFilterPlaceholder: string,
  onSave: func,
  onCancel: func,
  onChange: func,
  hideActions: bool
};

const MAX_FILTERS_LIMIT = 10;
const FILTERS_SCROLL_LIMIT = 5;

function FieldFilters(props) {
  const [elementFilters, setElementFilters] = useState(); // fieldPk, operator, text

  useEffect(() => {
    setElementFilters(getInitialElementRows());
  }, []);

  useEffect(() => {
    if (elementFilters && props.loaded) {
      const newValidFilters = elementFilters?.filter(filter => isFilterValid(filter));
      if (!isEqual(props.currentFilters, newValidFilters)) {
        onChange();
      }
    }
  }, [elementFilters]);

  const elementNames = useMemo(
    () =>
      uniq(
        sortBy(
          props.elements?.map(e => {
            return e.text;
          })
        )
      ),
    [props.elements]
  );

  function getInitialElementRows() {
    if (!props.currentFilters || props.currentFilters?.length === 0) {
      return [buildEmptyRow()];
    } else {
      return [...props.currentFilters];
    }
  }

  function buildEmptyRow() {
    return {
      element: '',
      operator: 'like',
      value: '',
      type: 'text',
      sourceField: '',
      dataSource: ''
    };
  }

  const isValid = useMemo(() => {
    const allValid = areAllFiltersValid();

    const onlyOneFilter = elementFilters?.length === 1;
    const first = elementFilters?.[0];
    let onlyOneAndEmpty = false;

    // there will always be a minimum of one row in the modal (if you delete it, a new, empty row will take its place)
    // So, if there's only one row and its inputs are empty, let the user save (same as saving nothing)
    if (onlyOneFilter && (first?.operator === 'isNull' || first?.operator === 'isNotNull')) {
      onlyOneAndEmpty = first?.element === '';
    } else if (onlyOneFilter) {
      onlyOneAndEmpty = first?.element === '' && first?.value === '';
    }

    return allValid || onlyOneAndEmpty;
  }, [elementFilters]);

  function addFilter() {
    if (elementFilters.length < MAX_FILTERS_LIMIT) {
      const newElementFilters = cloneDeep(elementFilters);
      newElementFilters.push(buildEmptyRow());
      setElementFilters(newElementFilters);
    }
  }

  function getElementRows() {
    const rows = [];
    elementFilters?.forEach((filter, index) => {
      rows.push(
        <div className="field-filter__element-row" key={`element-row-${index}`}>
          {getElementDropdown(index)}
          {getOperatorDropdown(index)}
          {getElementInput(index)}
          <i
            className="icon-trash"
            onClick={() => {
              const newElementFilters = cloneDeep(elementFilters);
              newElementFilters.splice(index, 1);

              if (newElementFilters.length === 0) {
                newElementFilters.push(buildEmptyRow());
              }

              setElementFilters(newElementFilters);
            }}
          />
        </div>
      );
    });

    return rows;
  }

  function getElementDropdown(index) {
    return (
      <AutoCompleteWrapper
        allSuggestions={elementNames}
        onChange={autoCompleteValue => {
          const field = props.elements?.find(e => e.text === autoCompleteValue);
          const fieldCode = field?.field_type_code;
          const type = fieldCode === 'text' ? 'text' : 'number';
          const sourceField = field?.source_field;
          const dataSource = field?.nonaggregate_data_sources?.[0];

          const newElementFilters = cloneDeep(elementFilters);
          if (newElementFilters[index].type !== type) {
            newElementFilters[index].operator = type === 'number' ? 'gt' : 'like';
          }
          newElementFilters[index] = {
            ...newElementFilters[index],
            element: autoCompleteValue,
            type,
            sourceField,
            dataSource,
            value: type === 'text' ? '' : null
          };

          setElementFilters(newElementFilters);
        }}
        placeholder="Select a Field"
        value={elementFilters[index].element}
      />
    );
  }

  function areAllFiltersValid() {
    return elementFilters?.every(filter => isFilterValid(filter));
  }

  function getValidFilters() {
    return elementFilters?.filter(filter => isFilterValid(filter));
  }

  function onChange() {
    const filtersToSave = getValidFilters();
    props.onChange && props.onChange(filtersToSave);
  }

  function getOperatorDropdown(index) {
    const operatorRef = React.createRef();
    const options = elementFilters[index].type === 'text' ? textOptions : numberOptions;

    return (
      <Select
        className="field-filter__operator-dropdown"
        value={options.find(o => o.key === elementFilters[index].operator)?.text}
        options={options}
        handleChange={operatorValue => {
          const newElementFilters = cloneDeep(elementFilters);
          newElementFilters[index].operator = options.find(o => o.text === operatorValue)?.key;

          // if isNull or isNotNull, we don't need to enter anything for the value input
          // clear it out here and disable the input for this row
          if (newElementFilters[index].operator === 'isNull' || newElementFilters[index].operator === 'isNotNull') {
            newElementFilters[index].value = '';
          }

          setElementFilters(newElementFilters);
        }}
        componentRef={operatorRef}
      />
    );
  }

  function getElementInput(index) {
    const isDisabled = elementFilters[index].operator === 'isNull' || elementFilters[index].operator === 'isNotNull';
    const placeholder = isDisabled ? '' : 'search within this field';

    // If element is a text field, render a normal text input
    // else render a number input field that will format inputs with commas
    if (elementFilters[index].type === 'text') {
      return (
        <Input
          className="field-filter__element-input"
          value={elementFilters[index].value}
          onChange={(event, value) => {
            const newElementFilters = cloneDeep(elementFilters);
            newElementFilters[index].value = value;
            setElementFilters(newElementFilters);
          }}
          type="text"
          placeholder={placeholder}
          disabled={isDisabled}
        />
      );
    } else {
      return (
        <Input
          className="field-filter__element-input"
          value={elementFilters[index].value}
          onChange={(event, value) => {
            const newElementFilters = cloneDeep(elementFilters);
            newElementFilters[index].value = value;
            setElementFilters(newElementFilters);
          }}
          onBlur={(event, value) => {
            const newElementFilters = cloneDeep(elementFilters);
            newElementFilters[index].value = value;
            setElementFilters(newElementFilters);
          }}
          type="number"
          displayFormatted={true}
          minNumberOfDecimals={0}
          maxNumberOfDecimals={3}
          placeholder={placeholder}
          disabled={isDisabled}
        />
      );
    }
  }

  function onCancel() {
    setElementFilters(getInitialElementRows());
    props.onCancel && props.onCancel();
  }

  function onSave() {
    // filter out invalid filters
    const filtersToSave = getValidFilters();
    props.onSave && props.onSave(filtersToSave);
  }

  function isFilterValid(e) {
    if (e.operator === 'isNull' || e.operator === 'isNotNull') {
      return !!e.element;
    }

    return e.element && e.operator && e.value !== '' && e.value != null;
  }

  return (
    <div className="field-filters">
      {props.loading ? (
        <InPlaceIndicator />
      ) : props.loaded && props.elements?.length === 0 ? (
        <Placeholder
          className="pxl-text-small pxl-text-muted"
          primaryText={props.noFilterPlaceholder || 'No Filter Elements Found'}
          showText={true}
        />
      ) : (
        <div className={cn('field-filter__rows', { more: elementFilters?.length >= FILTERS_SCROLL_LIMIT })}>
          {getElementRows()}

          {elementFilters?.length < MAX_FILTERS_LIMIT && (
            <div className="field-filter__add" onClick={addFilter}>
              + Add Filter
            </div>
          )}

          {!props.hideActions && (
            <div className="field-filter__bottom-row">
              <ButtonGroup>
                <Button variant="secondary" onClick={onCancel}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={onSave} disabled={!isValid}>
                  Save
                </Button>
              </ButtonGroup>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FieldFilters;
